<template>
  <!-- Dev: the login gate is bypassed so `pnpm dev` never blocks (unless the
       gated config is forced on via VITE_STUDIO_GATED to test locally). -->
  <StudioShell v-if="bypassGate" />

  <!-- Completing a Discord OAuth redirect. -->
  <div v-else-if="completing" class="gate">
    <SigdexMark class="gate__mark" />
    <p class="gate__text">Signing you in…</p>
  </div>

  <!-- Authenticated and permitted: the studio. -->
  <StudioShell v-else-if="isAuthenticated && hasAccess" />

  <!-- Authenticated but lacks studio access. -->
  <div v-else-if="isAuthenticated && !hasAccess" class="gate">
    <SigdexMark class="gate__mark" />
    <h1 class="gate__title">Access denied</h1>
    <p class="gate__text">Your account doesn't have studio access.</p>
    <StButton variant="secondary" @click="signOut">Sign out</StButton>
  </div>

  <!-- Not authenticated: login. -->
  <StudioLogin v-else />
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import StudioShell from './components/StudioShell.vue';
import StudioLogin from './components/StudioLogin.vue';
import SigdexMark from './components/SigdexMark.vue';
import { StButton } from './components/ui/index.js';
import { client, isAuthenticated, hasAccess } from './client.js';
import { isGated } from './env.js';
import { useLibrary } from './state/library.js';

declare const __PKG_VERSION__: string;
void __PKG_VERSION__;

// Login is required in the gated (production) config; bypassed otherwise.
const bypassGate = !isGated;
const completing = ref(false);

const lib = useLibrary();

function signOut() {
  client.logout();
}

// If we've landed on the Discord OAuth callback, exchange the code for tokens.
async function handleDiscordCallback() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (!code) return;

  completing.value = true;
  try {
    await client.completeDiscordLogin(code, params.get('state'));
  } catch {
    // Failure leaves us unauthenticated → the login page shows. The user can
    // retry; surfacing the raw OAuth error here isn't actionable.
  } finally {
    // Strip the OAuth query params so a refresh doesn't re-trigger the exchange.
    window.history.replaceState(
      {},
      '',
      window.location.pathname.replace(/\/discord\/login-callback$/, '/'),
    );
    completing.value = false;
  }
}

onMounted(async () => {
  if (!bypassGate) await handleDiscordCallback();
  lib.init();
});
</script>

<style scoped>
.gate {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--st-space-4);
  background: var(--st-shell-bg);
  text-align: center;
  padding: var(--st-space-5);
}
.gate__mark {
  height: 3rem;
  color: var(--st-accent);
}
.gate__title {
  font-family: var(--st-font-display);
  font-size: var(--st-text-xl);
  color: var(--st-shell-text);
}
.gate__text {
  font-size: var(--st-text-sm);
  color: var(--st-text-subtle);
}
</style>
